import { useState, useRef, useEffect, useCallback, useMemo } from "react";

interface UseInfiniteScrollOptions {
  pageSize?: number;
  loadDelay?: number;
}

export function useInfiniteScroll<T>(
  items: T[],
  options: UseInfiniteScrollOptions = {}
) {
  const { pageSize = 15, loadDelay = 300 } = options;
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // reset when the source array changes (filter/search/category switch)
  useEffect(() => {
    setVisibleCount(pageSize);
    setIsLoadingMore(false);
    loadingRef.current = false;
  }, [items, pageSize]);

  const hasMore = visibleCount < (items?.length ?? 0);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setIsLoadingMore(true);

    // brief delay so the spinner is visible (data is already in memory)
    setTimeout(() => {
      setVisibleCount((prev) => Math.min(prev + pageSize, items?.length ?? 0));
      setIsLoadingMore(false);
      loadingRef.current = false;
    }, loadDelay);
  }, [hasMore, pageSize, items?.length, loadDelay]);

  // callback ref — observer is set up the moment the sentinel mounts
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        },
        { rootMargin: "0px 0px 200px 0px" }
      );

      observerRef.current.observe(node);
    },
    [loadMore]
  );

  // clean up on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const visibleItems = useMemo(
    () => (items ?? []).slice(0, visibleCount),
    [items, visibleCount]
  );

  return { visibleItems, hasMore, isLoadingMore, sentinelRef };
}
